import React, { FC, useEffect, useRef, useState } from 'react';
import { MapContainer, MapContainerProps, Marker, TileLayer, useMap } from 'react-leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import * as L from 'leaflet';

import 'leaflet/dist/leaflet.css';
import markerSvg from '~shared/assets/marker.svg';

export interface OpenStreetMapViewProps extends MapContainerProps {
  coords: [number, number];
  zoom?: number;
  width?: string | number;
  height?: string | number;
  ClickHandler?: React.ReactNode;
  MapUpdater?: React.ReactNode;
  innerBounds?: [[number, number], [number, number]];
  outerBounds?: [[number, number], [number, number]];
  markerIcon?: any;
  scrollWheelZoom?: boolean;
  gestureHandling?: boolean;
  hideMarker?: boolean;
}

export const OpenStreetMapView: FC<OpenStreetMapViewProps> = ({
  ClickHandler,
  MapUpdater,
  coords = [41.3, 74.563],
  zoom = 13,
  width,
  height,
  innerBounds = [
    [39, 69.25],
    [44.5, 80.2675],
  ],
  outerBounds = [
    [39, 69],
    [44.5, 80],
  ],
  markerIcon = (L as any).icon({
    iconUrl: markerSvg,
    iconSize: [30, 40],
    iconAnchor: [17, 46], //[left/right, top/bottom]
    popupAnchor: [0, -46], //[left/right, top/bottom]
  }),
  scrollWheelZoom = false,
  gestureHandling = true,
  hideMarker = false,
  ...props
}) => {
  // const [coords, setCoords] = useState<[number, number]>([41.3, 74.563]);

  const [init, setInit] = useState<boolean>(false);
  const [showHint, setShowHint] = useState<boolean>(false);
  const [scrolling, setScrolling] = useState<boolean>(false); // Добавляем состояние для отслеживания прокрутки

  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const GestureHandlingSetter = () => {
    const map = useMap() as any;
    map.gestureHandling.enable();
    map.addHandler('gestureHandling', GestureHandling);
    setInit(false);

    return null;
  };

  const handleWheel = (e: WheelEvent) => {
    // Проверяем, не нажата ли клавиша Ctrl
    if (!e.ctrlKey) {
      setShowHint(true);
      setScrolling(true); // Начинаем отслеживать прокрутку
    } else {
      setShowHint(false);
      setScrolling(false); // Если Ctrl нажата, сбрасываем
    }
  };

  const handleStopScrolling = () => {
    setTimeout(() => {
      setShowHint(false);
      setScrolling(false);
    }, 500);
  };

  useEffect(() => {
    const mapContainer = mapContainerRef.current;

    if (mapContainer) {
      mapContainer.addEventListener('wheel', handleWheel);
      mapContainer.addEventListener('wheel', handleStopScrolling);
    }

    return () => {
      const mapContainer = mapContainerRef.current;

      if (mapContainer) {
        mapContainer.removeEventListener('wheel', handleWheel);
        mapContainer.removeEventListener('wheel', handleStopScrolling);
      }
    };
  }, [scrolling]);

  return (
    <div ref={mapContainerRef} style={{ position: 'relative', width, height, zIndex: 5 }}>
      <MapContainer
        style={{ width, height }}
        bounds={innerBounds as any}
        {...({ maxBounds: outerBounds } as any)}
        {...({ center: coords } as any)}
        zoom={zoom}
        scrollWheelZoom={scrollWheelZoom}
        bounceAtZoomLimits
        doubleClickZoom
        gestureHandling={gestureHandling}
        minZoom={7}
        {...props}
      >
        <TileLayer
          {...({
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          } as any)}
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {init && <GestureHandlingSetter />}
        {!hideMarker && <Marker position={coords} {...({ icon: markerIcon } as any)}></Marker>}
        {MapUpdater && MapUpdater}
        {ClickHandler && ClickHandler}

        {!ClickHandler && gestureHandling && (
          <div
            className={`absolute top-0 left-0 w-full h-full flex justify-center items-center transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg z-[500] transition-opacity duration-500 ${
              showHint ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="text-lg ">
              Чтобы изменить масштаб, зажмите <strong>Ctrl</strong> и прокручивайте колесо мыши.
            </div>
          </div>
        )}
      </MapContainer>
    </div>
  );
};
